<template>
    <div
        ref="dropdownRef"
        class="ui-dropdown"
        :class="{ 'ui-as-modal-on-mobile': asModalOnMobile }"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div
            class="ui-dropdown__trigger"
            @click.stop="!isHover ? toggleDropdown() : null"
        >
            <slot name="trigger">
                <button class="ui-dropdown-trigger__action-button" @click="onTriggerClick">
                    <img v-svg-inline src="@/assets/icons/ui/menu.svg" />
                </button>
            </slot>
        </div>

        <div
            v-if="isOpen"
            class="ui-dropdown__menu"
            :style="menuStyle"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @click.stop="handleMenuClick"
        >
            <ul
                v-if="$slots.elements"
                class="ui-dropdown-menu"
            >
                <slot name="elements"></slot>
            </ul>

            <slot v-if="$slots.menu" name="menu"></slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, onBeforeUnmount, nextTick, defineEmits, useSlots, h, watch, computed } from 'vue';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { ModalPluginType } from '@/plugins/modalPlugin.ts';
    import { ModalKey } from '@/injection/keys.ts';

    import ActionsModal from '@/modals/ActionsModal/ActionsModal.vue';

    const props = defineProps({
        isHover: {
            type: Boolean,
            default: false,
        },
        additionalTop: {
            type: Number,
            default: 0,
        },
        additionalLeft: {
            type: Number,
            default: 0,
        },
        alignment: {
            type: String,
            default: 'right',
            validator: (value: string) => ['left', 'right'].includes(value),
        },
        asModalOnMobile: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits<{
        (e: 'click'): void,
    }>();

    const slots = useSlots();

    const $modal = injectStrict<ModalPluginType>(ModalKey);

    const isOpen = ref(false);
    const dropdownRef = ref<HTMLElement | null>(null);
    const menuStyle = ref<Record<string, string>>({});
    const lastMenuStyle = ref<Record<string, string>>({});
    const closeTimeout = ref<number | null>(null);

    const openAsModal = computed<boolean>(() => {
        return props.asModalOnMobile && window.screen.width <= 768;
    });

    watch(
        () => isOpen.value,
        (newValue) => {
            if (openAsModal.value && newValue) {
                openModal();
            }
        },
    );

    function toggleDropdown(): void {
        if (!isOpen.value) {
            document.dispatchEvent(new CustomEvent('close-all-dropdowns'));
        }

        isOpen.value = !isOpen.value;

        if (isOpen.value) {
            nextTick(() => {
                calculateMenuPosition();
            });
        }
    }

    function openDropdown(): void {
        if (closeTimeout.value) {
            clearTimeout(closeTimeout.value);
            closeTimeout.value = null;
        }

        isOpen.value = true;

        nextTick(() => {
            calculateMenuPosition();
        });
    }

    function closeDropdown(): void {
        closeTimeout.value = window.setTimeout(() => {
            isOpen.value = false;
            lastMenuStyle.value = { ...menuStyle.value };
        }, 100);
    }

    function openModal(): void {
        $modal.open({
            component: ActionsModal,
            slots: {
                default: () => {
                    return h('div', slots.elements ? slots.elements() : '');
                },
            },
            onClose() {
                isOpen.value = false;
            },
        });
    }

    function handleMouseEnter(): void {
        if (props.isHover) {
            openDropdown();
        }
    }

    function handleMouseLeave(): void {
        if (props.isHover) {
            closeDropdown();
        }
    }

    function onTriggerClick(): void {
        emit('click');
    }

    // Закрытие меню при клике на элемент
    function handleMenuClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (target.closest('.ui-dropdown__menu')) {
            closeDropdown();
        }
    }

    function calculateMenuPosition(): void {
        const dropdown = dropdownRef.value;
        if (!dropdown) {
            return;
        }

        const trigger = dropdown.querySelector('.ui-dropdown__trigger') as HTMLElement;
        const menu = dropdown.querySelector('.ui-dropdown__menu') as HTMLElement;

        if (!trigger || !menu) {
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        const horizontalPosition: Record<string, string> = {};
        if (props.alignment === 'right') {
            horizontalPosition.right = '0px';
        }
        else {
            horizontalPosition.left = `${triggerRect.left - dropdownRect.left + props.additionalLeft}px`;
        }

        menuStyle.value = {
            ...horizontalPosition,
            top: `${triggerRect.bottom - dropdownRect.top + props.additionalTop}px`,
            position: 'absolute',
        };
    }

    function handleClickOutside(event: MouseEvent): void {
        if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
            isOpen.value = false;
            lastMenuStyle.value = { ...menuStyle.value };
        }
    }

    function closeAllDropdowns(): void {
        isOpen.value = false;
    }

    onMounted(() => {
        if (!props.isHover) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('close-all-dropdowns', closeAllDropdowns);
        }
    });

    onBeforeUnmount(() => {
        if (!props.isHover) {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('close-all-dropdowns', closeAllDropdowns);
        }

        if (closeTimeout.value) {
            clearTimeout(closeTimeout.value);
        }
    });
</script>

<style lang="scss">
    @use './UIDropDown.scss' as *;
</style>
